import React, {useContext, useState} from "react";
import Grid from "@mui/material/GridLegacy";
import TextField from "@mui/material/TextField";
import {Control, UseFormRegister} from "react-hook-form/dist/types/form";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {Controller} from "react-hook-form";
import {FormControl} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import {IState} from "../../../../queries/pharmacy/pharmacyModel";
import Typography from "@mui/material/Typography";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {red} from "@mui/material/colors";
import {ToasterContext} from "../../../shared/Toaster/ToasterContext";
import {NotificationType} from "../../../../constants/global";
import LicenseUploadCard from "../../../shared/LicenseUploadCard/LicenseUploadCard";

interface PharmacyDetailsProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  control: Control<any>;
  states: IState[];
  setDeaLicenseFile: any;
  deaLicenseFile: any;
  setNcpdpLicenseFile: any;
  ncpdpLicenseFile: any;
  businessDocumentFile: any;
  setBusinessDocumentFile: any;
}

const PharmacyDetails:React.FunctionComponent<PharmacyDetailsProps> = ({
  register,
  errors,
  control,
  states,
  setDeaLicenseFile,
  deaLicenseFile,
  setNcpdpLicenseFile,
  ncpdpLicenseFile,
  businessDocumentFile,
  setBusinessDocumentFile,
}) => {
  const {addToast} = useContext(ToasterContext);
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
  
  // Error states for each file input
  const [deaLicenseError, setDeaLicenseError] = useState<string>('');
  const [ncpdpLicenseError, setNcpdpLicenseError] = useState<string>('');
  const [businessDocumentError, setBusinessDocumentError] = useState<string>('');

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      return false;
    }
    return true;
  };

  const validateFileType = (file: File): boolean => {
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
    const allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const fileMimeType = file.type.toLowerCase();
    
    // Check if file extension is HEIC/HEIF
    if (fileExtension === 'heic' || fileExtension === 'heif') {
      return false;
    }
    
    // Check if MIME type is HEIC/HEIF
    if (fileMimeType.includes('heic') || fileMimeType.includes('heif')) {
      return false;
    }
    
    // Check if file extension is allowed
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return false;
    }
    
    // Check if MIME type is allowed (if available)
    if (fileMimeType && !allowedMimeTypes.includes(fileMimeType)) {
      return false;
    }
    
    return true;
  };

  const handleDeaLicenseFileChange = (file: File) => {
    setDeaLicenseError('');

    if (!validateFileType(file)) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'heic' || fileExtension === 'heif') {
        setDeaLicenseError('HEIC files are not supported. Please convert to JPG, PNG, or PDF format.');
      } else {
        setDeaLicenseError('File type not supported. Please select JPG, PNG, or PDF files only.');
      }
      return;
    }

    if (!validateFileSize(file)) {
      setDeaLicenseError(`File "${file.name}" is too large. Maximum size is 15 MB.`);
      return;
    }

    const newFile = new File([file], `${Date.now()}_${file.name}`, {
      type: file.type,
    });

    setDeaLicenseFile(newFile);
  }
  
  const handleNcpdpLicenseFileChange = (file: File) => {
    setNcpdpLicenseError('');

    if (!validateFileType(file)) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'heic' || fileExtension === 'heif') {
        setNcpdpLicenseError('HEIC files are not supported. Please convert to JPG, PNG, or PDF format.');
      } else {
        setNcpdpLicenseError('File type not supported. Please select JPG, PNG, or PDF files only.');
      }
      return;
    }

    if (!validateFileSize(file)) {
      setNcpdpLicenseError(`File "${file.name}" is too large. Maximum size is 15 MB.`);
      return;
    }

    const newFile = new File([file], `${Date.now()}_${file.name}`, {
      type: file.type,
    });

    setNcpdpLicenseFile(newFile);
  }

  const handleBusinessDocumentFileChange = (file: File) => {
    setBusinessDocumentError('');

    if (!validateFileType(file)) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'heic' || fileExtension === 'heif') {
        setBusinessDocumentError('HEIC files are not supported. Please convert to JPG, PNG, or PDF format.');
      } else {
        setBusinessDocumentError('File type not supported. Please select JPG, PNG, or PDF files only.');
      }
      return;
    }

    if (!validateFileSize(file)) {
      setBusinessDocumentError(`File "${file.name}" is too large. Maximum size is 15 MB.`);
      return;
    }

    const newFile = new File([file], `${Date.now()}_${file.name}`, {
      type: file.type,
    });

    setBusinessDocumentFile(newFile);
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sx={{m: 2}}>
        <Typography component="h1" variant="h5">
          Pharmacy Details
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Legal Name"
          id="legalName"
          fullWidth
          required
          helperText={errors.legalName?.message?.toString()}
          {...register('legalName')}
          error={!!errors.legalName}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Software Name"
          id="softwareName"
          fullWidth
          required
          helperText={errors.softwareName?.message?.toString()}
          {...register('softwareName')}
          error={!!errors.softwareName}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="ncpdp"
          label="Pharmacy NCPDP"
          id="ncpdp"
          fullWidth
          required
          helperText={errors.ncpdp?.message?.toString()}
          {...register('ncpdp')}
          error={!!errors.ncpdp}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="npi"
          label="Pharmacy NPI"
          id="npi"
          fullWidth
          required
          helperText={errors.npi?.message?.toString()}
          {...register('npi')}
          error={!!errors.npi}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="deaNumber"
          label="DEA Number"
          id="deaNumber"
          fullWidth
          required
          helperText={errors.deaNumber?.message?.toString()}
          {...register('deaNumber')}
          error={!!errors.deaNumber}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="verifyDeaNumber"
          label="Verify DEA Number"
          id="verifyDeaNumber"
          fullWidth
          required
          helperText={errors.verifyDeaNumber?.message?.toString()}
          {...register('verifyDeaNumber')}
          error={!!errors.verifyDeaNumber}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          name="stateLicense"
          control={control}
          defaultValue=''
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="stateLicense-label">Pharmacy License</InputLabel>
              <Select
                {...field}
                labelId="stateLicense-label"
                id="stateLicense"
                label="Pharmacy License"
                fullWidth
                required
                {...register('stateLicense')}
                error={!!errors.stateLicense}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
              >
                {states.map((state: IState) => (
                  <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="federalTaxId"
          label="Federal Tax ID"
          id="federalTaxId"
          fullWidth
          required
          helperText={errors.federalTaxId?.message?.toString()}
          {...register('federalTaxId')}
          error={!!errors.federalTaxId}
        />
      </Grid>
      <Grid item xs={12} sx={{mt: 2}}>
        <FormLabel id="is340B-radios">Please make sure the document you’re about to upload meets the requirements below. If it does, please confirm by checking:</FormLabel>
        <Grid item xs={12}>
          <Controller
            name="businessName"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={(
                  <Checkbox
                    sx={{color: errors.businessName ? red[500] : 'inherit'}}
                    checked={value}
                    onChange={onChange}
                  />
                )}
                label="The document shows your business name exactly as provided."
                sx={{color: errors.businessName ? red[500] : 'inherit'}}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="ein"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={(
                  <Checkbox
                    sx={{color: errors.ein ? red[500] : 'inherit'}}
                    checked={value}
                    onChange={onChange}
                  />
                )}
                label="The document shows your EIN exactly as provided."
                sx={{color: errors.ein ? red[500] : 'inherit'}}
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{m: 2}}>
        <Typography component="h1" variant="h5">
          Contact Info
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="email"
          label="Email Address"
          id="email"
          fullWidth
          required
          {...register('email')}
          helperText={errors.email?.message?.toString()}
          error={!!errors.email}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="phone"
          label="Phone"
          id="phone"
          fullWidth
          required
          {...register('phone')}
          helperText={errors.phone?.message?.toString()}
          error={!!errors.phone}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="mobile"
          label="Mobile"
          id="mobile"
          fullWidth
          required
          {...register('mobile')}
          helperText={errors.mobile?.message?.toString()}
          error={!!errors.mobile}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="site"
          label="Website"
          id="site"
          fullWidth
          {...register('site')}
          helperText={errors.site?.message?.toString()}
          error={!!errors.site}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="store"
          label="Number of Stores"
          id="store"
          fullWidth
          required
          {...register('store')}
          helperText={errors.store?.message?.toString()}
          error={!!errors.store}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete="fax"
          label="Fax"
          id="fax"
          fullWidth
          required
          {...register('fax')}
          helperText={errors.fax?.message?.toString()}
          error={!!errors.fax}
        />
      </Grid>
      <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
        <LicenseUploadCard
          title="Upload DEA License"
          fileName={deaLicenseFile?.name}
          error={deaLicenseError}
          onFileSelect={handleDeaLicenseFileChange}
        />
        <LicenseUploadCard
          title="Upload Pharmacy License"
          fileName={ncpdpLicenseFile?.name}
          error={ncpdpLicenseError}
          onFileSelect={handleNcpdpLicenseFileChange}
        />
        <LicenseUploadCard
          title="147c (IRS confirmation)"
          fileName={businessDocumentFile?.name}
          error={businessDocumentError}
          onFileSelect={handleBusinessDocumentFileChange}
        />
      </Grid>
    </Grid>
  )
}

export default PharmacyDetails;